import React, { useState, useEffect, useRef } from "react";
import { ref, onValue, update } from "firebase/database";
import { storage, db } from "../firebaseConfig";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const AppPage = () => {
  const [workspaces, setWorkspaces] = useState({});
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  const [workspaceDetails, setWorkspaceDetails] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [selectedTextLogoFile, setSelectedTextLogoFile] = useState(null);
  const [selectedBackgroundLandscapeFile, setSelectedBackgroundLandscapeFile] = useState(null);
  const [selectedBackgroundPortraitFile, setSelectedBackgroundPortraitFile] = useState(null);
  const [apkFile, setApkFile] = useState(null);
  const apkFileInputRef = useRef(null);
  const logoFileInputRef = useRef(null);
  const textLogoFileInputRef = useRef(null);
  const backgroundLandscapeFileInputRef = useRef(null);
  const backgroundPortraitFileInputRef = useRef(null);

  useEffect(() => {
    const dbRef = ref(db, "neoslide/workspaces");

    onValue(dbRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setWorkspaces(data);
      }
    });
  }, []);

  useEffect(() => {
    if (selectedWorkspace) {
      const dbRef = ref(db, `neoslide/app/${selectedWorkspace}`);

      onValue(dbRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setWorkspaceDetails(data);
          setFormData(data);
          fetchApkFile(selectedWorkspace, data.apkName);
        } else {
          const defaultValues = {
            activateRebootAlarm: false,
            airSyncIntervalMinutes: 360,
            monitoringCount: 5,
            monitoringIntervalSeconds: 5,
            weatherSyncIntervalMinutes: 360,
            webAdminLogo: "",
            webAdminTextLogo: "",
            backgroundLandscapeUrl: "",
            backgroundPortraitUrl: "",
            apkName: "",
            appVersion: ""
          };
          setWorkspaceDetails(defaultValues);
          setFormData(defaultValues);
          setApkFile(null);
        }
      });
    }
  }, [selectedWorkspace]);

  useEffect(() => {
    setSelectedFile(null);
    setSelectedLogoFile(null);
    setSelectedTextLogoFile(null);
    setSelectedBackgroundLandscapeFile(null);
    setSelectedBackgroundPortraitFile(null);

    if (apkFileInputRef.current) apkFileInputRef.current.value = "";
    if (logoFileInputRef.current) logoFileInputRef.current.value = "";
    if (textLogoFileInputRef.current) textLogoFileInputRef.current.value = "";
    if (backgroundLandscapeFileInputRef.current) backgroundLandscapeFileInputRef.current.value = "";
    if (backgroundPortraitFileInputRef.current) backgroundPortraitFileInputRef.current.value = "";
  }, [selectedWorkspace]);

  const fetchApkFile = async (workspace, apkName) => {
    if (!apkName) {
      setApkFile(null);
      return;
    }

    const apkRef = storageRef(storage, `clients/${workspace}/Apks/${apkName}`);
    try {
      const apkUrl = await getDownloadURL(apkRef);
      setApkFile({ apkUrl, apkName });
    } catch (error) {
      console.error("Error fetching APK file:", error);
      setApkFile(null);
    }
  };

  const handleWorkspaceChange = (e) => {
    const selectedKey = e.target.value;
    setSelectedWorkspace(selectedKey);
  };

  const handleInputChange = (field, value) => {
    if (
      field === "airSyncIntervalMinutes" ||
      field === "monitoringCount" ||
      field === "monitoringIntervalSeconds" ||
      field === "weatherSyncIntervalMinutes"
    ) {
      if (value < 1) {
        value = 1;
      }
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedLogoFile(file);
    }
  };

  const handleTextLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedTextLogoFile(file);
    }
  };

  const handleBackgroundLandscapeFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedBackgroundLandscapeFile(file);
    }
  };

  const handleBackgroundPortraitFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedBackgroundPortraitFile(file);
    }
  };

  const deleteOldFile = async (fileUrl) => {
    if (!fileUrl) return;

    try {
      const filePath = decodeURIComponent(fileUrl.split("/o/")[1].split("?")[0]);
      const fileRef = storageRef(storage, filePath);
      await deleteObject(fileRef);
    } catch (error) {
      console.error("Failed to delete old file:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedWorkspace) return;

    let webAdminLogoUrl = formData.webAdminLogo || "";
    let webAdminTextLogoUrl = formData.webAdminTextLogo || "";
    let backgroundLandscapeUrl = formData.backgroundLandscapeUrl || "";
    let backgroundPortraitUrl = formData.backgroundPortraitUrl || "";

    setUploading(true);

    const updatedFormData = {
      ...formData,
      airSyncIntervalMinutes: formData.airSyncIntervalMinutes || 360,
      monitoringCount: formData.monitoringCount || 5,
      monitoringIntervalSeconds: formData.monitoringIntervalSeconds || 5,
      weatherSyncIntervalMinutes: formData.weatherSyncIntervalMinutes || 360,
    };

    if (selectedLogoFile && formData.webAdminLogo) {
      await deleteOldFile(formData.webAdminLogo);
    }
    if (selectedTextLogoFile && formData.webAdminTextLogo) {
      await deleteOldFile(formData.webAdminTextLogo);
    }
    if (selectedBackgroundLandscapeFile && formData.backgroundLandscapeUrl) {
      await deleteOldFile(formData.backgroundLandscapeUrl);
    }
    if (selectedBackgroundPortraitFile && formData.backgroundPortraitUrl) {
      await deleteOldFile(formData.backgroundPortraitUrl);
    }

    if (selectedFile) {
      const filePath = `clients/${selectedWorkspace}/Apks/${selectedFile.name}`;
      const fileRef = storageRef(storage, filePath);

      try {
        await uploadBytes(fileRef, selectedFile);
        const apkUrl = await getDownloadURL(fileRef);
        updatedFormData.apkName = selectedFile.name;
        setApkFile({ apkUrl, apkName: selectedFile.name });
      } catch (error) {
        console.error("APK file upload failed", error);
        setUploading(false);
        return;
      }
    }

    if (selectedLogoFile) {
      const filePath = `clients/${selectedWorkspace}/AppFiles/${selectedLogoFile.name}`;
      const fileRef = storageRef(storage, filePath);

      try {
        await uploadBytes(fileRef, selectedLogoFile);
        webAdminLogoUrl = await getDownloadURL(fileRef);
      } catch (error) {
        console.error("Logo file upload failed", error);
        setUploading(false);
        return;
      }
    }

    if (selectedTextLogoFile) {
      const filePath = `clients/${selectedWorkspace}/AppFiles/${selectedTextLogoFile.name}`;
      const fileRef = storageRef(storage, filePath);

      try {
        await uploadBytes(fileRef, selectedTextLogoFile);
        webAdminTextLogoUrl = await getDownloadURL(fileRef);
      } catch (error) {
        console.error("Text logo file upload failed", error);
        setUploading(false);
        return;
      }
    }

    if (selectedBackgroundLandscapeFile) {
      const filePath = `clients/${selectedWorkspace}/Backgrounds/${selectedBackgroundLandscapeFile.name}`;
      const fileRef = storageRef(storage, filePath);

      try {
        await uploadBytes(fileRef, selectedBackgroundLandscapeFile);
        backgroundLandscapeUrl = await getDownloadURL(fileRef);
      } catch (error) {
        console.error("Background landscape file upload failed", error);
        setUploading(false);
        return;
      }
    }

    if (selectedBackgroundPortraitFile) {
      const filePath = `clients/${selectedWorkspace}/Backgrounds/${selectedBackgroundPortraitFile.name}`;
      const fileRef = storageRef(storage, filePath);

      try {
        await uploadBytes(fileRef, selectedBackgroundPortraitFile);
        backgroundPortraitUrl = await getDownloadURL(fileRef);
      } catch (error) {
        console.error("Background portrait file upload failed", error);
        setUploading(false);
        return;
      }
    }

    try {
      const updatedData = {
        ...updatedFormData,
        webAdminLogo: webAdminLogoUrl,
        webAdminTextLogo: webAdminTextLogoUrl,
        backgroundLandscapeUrl,
        backgroundPortraitUrl,
      };

      Object.keys(updatedData).forEach((key) => {
        if (updatedData[key] === undefined) {
          delete updatedData[key];
        }
      });

      await update(ref(db, `neoslide/app/${selectedWorkspace}`), updatedData);
      alert("Workspace updated successfully!");
      setSelectedFile(null);
      setSelectedLogoFile(null);
      setSelectedTextLogoFile(null);
      setSelectedBackgroundLandscapeFile(null);
      setSelectedBackgroundPortraitFile(null);
    } catch (error) {
      console.error("Update failed", error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="workspaces-container">
      <h2>App Page</h2>
      <select value={selectedWorkspace} onChange={handleWorkspaceChange}>
        <option value="" disabled>
          Select a workspace
        </option>
        {Object.entries(workspaces).map(([key, name]) => (
          <option key={key} value={name}>
            {name}
          </option>
        ))}
      </select>
      {workspaceDetails && (
        <form className="app-form">
          <div className="app-workspace-details">
            <h3>Workspace: {selectedWorkspace}</h3>
            <label className="checkbox">
              Activate Reboot Alarm
              <input
                type="checkbox"
                checked={formData.activateRebootAlarm || false}
                onChange={(e) => handleInputChange("activateRebootAlarm", e.target.checked)}
              />
            </label>
            <label className="checkbox">
              Air Sync Interval (minutes):
              <input
                type="number"
                value={formData.airSyncIntervalMinutes || ""}
                onChange={(e) => handleInputChange("airSyncIntervalMinutes", Math.max(1, Number(e.target.value)))}
                className="input-number"
                min="1"
              />
            </label>
            <label className="checkbox">
              Monitoring Count:
              <input
                type="number"
                value={formData.monitoringCount || ""}
                onChange={(e) => handleInputChange("monitoringCount", Math.max(1, Number(e.target.value)))}
                className="input-number"
                min="1"
              />
            </label>
            <label className="checkbox">
              Monitoring Interval (seconds):
              <input
                type="number"
                value={formData.monitoringIntervalSeconds || ""}
                onChange={(e) => handleInputChange("monitoringIntervalSeconds", Math.max(1, Number(e.target.value)))}
                className="input-number"
                min="1"
              />
            </label>
            <label className="checkbox">
              Weather Sync Interval (minutes):
              <input
                type="number"
                value={formData.weatherSyncIntervalMinutes || ""}
                onChange={(e) => handleInputChange("weatherSyncIntervalMinutes", Math.max(1, Number(e.target.value)))}
                className="input-number"
                min="1"
              />
            </label>
            <label className="checkbox">
              App Version:
              <input
                type="text"
                className="input-number"
                value={formData.appVersion || ""}
                onChange={(e) => handleInputChange("appVersion", e.target.value)}
              />
            </label>
            <label className="checkbox">
              APK File:
              <input
                type="file"
                accept=".apk"
                onChange={handleFileChange}
                disabled={uploading}
                ref={apkFileInputRef}
              />
            </label>
            <label className="checkbox">
              <p>
                Current APK:{" "}
                {apkFile ? (
                  <a href={apkFile.apkUrl} download={apkFile.apkName}>
                    {apkFile.apkName}
                  </a>
                ) : formData?.apkName}
              </p>
            </label>
            <label className="checkbox">
              Web Admin Logo:
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoFileChange}
                disabled={uploading}
                ref={logoFileInputRef}
              />
              {workspaceDetails?.webAdminLogo && (
                <div>
                  <img src={workspaceDetails.webAdminLogo} alt="Workspace Logo" width="150" />
                </div>
              )}
            </label>
            <label className="checkbox">
              Web Admin Text Logo:
              <input
                type="file"
                accept="image/*"
                onChange={handleTextLogoFileChange}
                disabled={uploading}
                ref={textLogoFileInputRef}
              />
              {workspaceDetails?.webAdminTextLogo && (
                <div>
                  <img src={workspaceDetails.webAdminTextLogo} alt="Workspace Text Logo" width="150" />
                </div>
              )}
            </label>
            <label className="checkbox">
              Background Landscape:
              <input
                type="file"
                accept="image/*"
                onChange={handleBackgroundLandscapeFileChange}
                disabled={uploading}
                ref={backgroundLandscapeFileInputRef}
              />
              {workspaceDetails?.backgroundLandscapeUrl && (
                <div>
                  <img src={workspaceDetails.backgroundLandscapeUrl} alt="Background Landscape" width="150" />
                </div>
              )}
            </label>
            <label className="checkbox">
              Background Portrait:
              <input
                type="file"
                accept="image/*"
                onChange={handleBackgroundPortraitFileChange}
                disabled={uploading}
                ref={backgroundPortraitFileInputRef}
              />
              {workspaceDetails?.backgroundPortraitUrl && (
                <div>
                  <img src={workspaceDetails.backgroundPortraitUrl} alt="Background Portrait" width="150" />
                </div>
              )}
            </label>
          </div>
          <button type="submit" onClick={handleSubmit} disabled={uploading}>
            Save Changes
          </button>
        </form>
      )
      }
    </div >
  );
};

export default AppPage;